import { TriangleAlertIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { useDeleteOverhead } from '../../hooks/use-delete-overhead';
import { OverheadListItem, OverheadStatus, OverheadType } from '../../types/overhead';

interface DeleteOverheadModalProps {
  overhead: OverheadListItem | null;
  onClose: () => void;
}

export const DeleteOverheadModal = ({ overhead, onClose }: DeleteOverheadModalProps) => {
  const { t } = useTranslation('authenticated.overheads', { keyPrefix: 'modals.deleteOverhead' });

  const deleteMutation = useDeleteOverhead({ onSuccess: onClose });

  if (!overhead) return null;

  const isPendingIncome =
    overhead.type === OverheadType.Income && overhead.status === OverheadStatus.Pending;
  // Invoiced overheads are immutable (backend rejects the delete with
  // OVERHEAD_ALREADY_INVOICED). The table disables the action, but the entry
  // can become invoiced after the list loaded — block the confirm and explain.
  const isInvoicedLocked = overhead.status === OverheadStatus.Invoiced;

  return (
    <ConfirmationModal
      title={t('title')}
      hideCloseIcon
      message={
        isInvoicedLocked ? (
          <Alert variant="warning">
            <AlertDescription className="flex items-center gap-1.5">
              <TriangleAlertIcon size={16} className="shrink-0" />
              {overhead.invoice?.number
                ? t('invoicedLocked', { number: overhead.invoice.number })
                : t('invoicedLockedNoNumber')}
            </AlertDescription>
          </Alert>
        ) : (
          <div>
            <Trans
              t={t}
              i18nKey="description"
              values={{ name: overhead.name }}
              components={{ bold: <strong /> }}
            />
            {isPendingIncome && <p className="mt-2">{t('pendingIncomeNote')}</p>}
            {overhead.seriesId !== null && <p className="mt-2">{t('recurringNote')}</p>}
          </div>
        )
      }
      confirmText={t('submit')}
      open={!!overhead}
      isLoading={deleteMutation.isPending}
      disabled={isInvoicedLocked}
      onClose={onClose}
      onConfirm={() => deleteMutation.mutate(overhead.id)}
    />
  );
};
